/**
 * Networks the web app knows by name. The site is built for ONE chain (VITE_CHAIN_ID); the deployment manifest and the
 * RPC must both report it, and wallets are asked to switch to it.
 */
import { defineChain, type Chain } from 'viem';

export interface NetworkInfo {
  id: number;
  name: string;
  /** The launch's quote token on this chain, known before any deployment (shown instead of a generic "quote"). */
  quote?: { symbol: string; decimals: number; address: `0x${string}`; label: string };
  kind: 'local' | 'testnet' | 'mainnet';
  explorer?: string;
}

const NETWORKS: Record<number, NetworkInfo> = {
  31337: { id: 31337, name: 'Anvil (local)', kind: 'local' },
  // Robinhood Chain's explorer is Blockscout (the explorer verification tooling targets the same host, D56).
  4663: {
    id: 4663,
    name: 'Robinhood Chain',
    kind: 'mainnet',
    explorer: 'https://robinhoodchain.blockscout.com',
    quote: { symbol: 'USDG', decimals: 6, address: '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168', label: 'USDG (Global Dollar, issued by Paxos)' },
  },
  46630: { id: 46630, name: 'Robinhood Chain Testnet', kind: 'testnet' },
  421614: { id: 421614, name: 'Arbitrum Sepolia', kind: 'testnet', explorer: 'https://sepolia.arbiscan.io' },
};

export function networkInfo(id: number): NetworkInfo {
  return NETWORKS[id] ?? { id, name: `Chain ${id}`, kind: 'testnet' };
}

/** The viem chain for `id`, reading through `rpcUrl`. */
export function chainFor(id: number, rpcUrl: string): Chain {
  const n = networkInfo(id);
  return defineChain({
    id,
    name: n.name,
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: { default: { http: [rpcUrl] } },
    ...(n.explorer ? { blockExplorers: { default: { name: `${n.name} explorer`, url: n.explorer } } } : {}),
  });
}
